import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { timeAgo } from '../utils/dateFormat';
import { useWS } from '../hooks/useWebSocket';

// LED state visual mapping — mirrors firmware ButtonManager states.
// Modern firmware reports the patient-state name (ok, checkin_window, …,
// not_configured) in led_state; the legacy color keys are kept for older
// builds that reported a raw color.
const LED_STATES = {
  default:     { label: 'Default',      color: 'bg-secondary-300',  ring: 'ring-secondary-200' },
  // Firmware patient states (ButtonManager::patientStateName)
  ok:               { label: 'OK',                color: 'bg-emerald-500', ring: 'ring-emerald-300' },
  checkin_window:   { label: 'Check-in Window',   color: 'bg-amber-400',   ring: 'ring-amber-300' },
  checkin_required: { label: 'Check-in Required', color: 'bg-red-500',     ring: 'ring-red-300', animate: true },
  degraded:         { label: 'Degraded',          color: 'bg-orange-500',  ring: 'ring-orange-300', animate: true },
  help_called:      { label: 'Help Called',       color: 'bg-red-700',     ring: 'ring-red-400', animate: true },
  vacation:         { label: 'Vacation',          color: 'bg-blue-400',    ring: 'ring-blue-300' },
  fallback_setup:   { label: 'Setup Mode',        color: 'bg-cyan-500',    ring: 'ring-cyan-300' },
  not_configured:   { label: 'Not Configured',    color: 'bg-orange-400',  ring: 'ring-orange-300', animate: true },
  // Legacy raw-color keys (pre-patient-state firmware)
  white:       { label: 'White',        color: 'bg-white border border-secondary-300', ring: 'ring-secondary-200' },
  off:         { label: 'Off',          color: 'bg-secondary-800',  ring: 'ring-secondary-400' },
  blue:        { label: 'Blue',         color: 'bg-blue-500',       ring: 'ring-blue-300' },
  flashingRed: { label: 'Flashing Red', color: 'bg-red-500',        ring: 'ring-red-300', animate: true },
  rapidRed:    { label: 'Rapid Red',    color: 'bg-red-600',        ring: 'ring-red-400', animate: true },
  solidRed:    { label: 'Solid Red',    color: 'bg-red-700',        ring: 'ring-red-400' },
};

function LEDIndicator({ state }) {
  const led = LED_STATES[state] || LED_STATES.default;
  return (
    <div className="flex items-center gap-2">
      <span
        className={`inline-block w-3 h-3 rounded-full ${led.color} ring-2 ${led.ring} ${led.animate ? 'animate-pulse' : ''}`}
        title={led.label}
      />
      <span className="text-xs text-secondary-600">{led.label}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: 'bg-emerald-50 text-emerald-700',
    inactive: 'bg-red-50 text-red-700',
    maintenance: 'bg-amber-50 text-amber-700',
    decommissioned: 'bg-secondary-100 text-secondary-500',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || map.active}`}>{status}</span>;
}

// Vacation is a boolean overlay flag (devices.vacation_mode), not a status enum
// value, so it renders as a separate pill alongside the StatusBadge.
function VacationBadge() {
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700" title="Vacation mode active">
      Vacation
    </span>
  );
}

function ProvisioningBadge({ status }) {
  const map = {
    unregistered:   'bg-secondary-100 text-secondary-500',
    registered:     'bg-amber-50 text-amber-700',
    provisioned:    'bg-cyan-50 text-cyan-700',
    claimed:        'bg-purple-50 text-purple-600',
    setup_complete: 'bg-emerald-50 text-emerald-700',
  };
  const labels = {
    unregistered:   'Not Seen',
    registered:     'Awaiting Setup',
    provisioned:    'Provisioned',
    claimed:        'Claimed',
    setup_complete: 'Setup Complete',
  };
  const key = status || 'unregistered';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[key] || map.unregistered}`}>{labels[key] || key}</span>;
}

function DeviceTable({ devices, loading, tab }) {
  const isRegistered = tab === 'registered';
  const colSpan = isRegistered ? 6 : 5;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary-50 text-secondary-600">
          <tr>
            <th className="px-5 py-3 text-left font-medium">Device ID</th>
            <th className="px-5 py-3 text-left font-medium">{isRegistered ? 'Patient' : 'Provisioning'}</th>
            <th className="px-5 py-3 text-left font-medium">Status</th>
            {isRegistered && <th className="px-5 py-3 text-left font-medium">LED State</th>}
            <th className="px-5 py-3 text-left font-medium">Last Seen</th>
            <th className="px-5 py-3 text-left font-medium">Firmware</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-secondary-100">
          {loading && (
            <tr><td colSpan={colSpan} className="px-5 py-8 text-center text-secondary-400">Loading...</td></tr>
          )}
          {!loading && devices.length === 0 && (
            <tr><td colSpan={colSpan} className="px-5 py-8 text-center text-secondary-400">No devices found</td></tr>
          )}
          {!loading && devices.map((d) => (
            <tr key={d.id} className="hover:bg-secondary-50 transition">
              <td className="px-5 py-3">
                <Link to={`/devices/${d.id}`} className="text-primary-600 hover:text-primary-700 font-medium">
                  {d.device_id}
                </Link>
              </td>
              <td className="px-5 py-3 text-secondary-700">
                {isRegistered
                  ? (d.config_patient_name || (d.patient_first_name ? `${d.patient_first_name} ${d.patient_last_name}` : '--'))
                  : <ProvisioningBadge status={d.provisioning_status} />
                }
              </td>
              <td className="px-5 py-3">
                <div className="flex items-center gap-1.5">
                  <StatusBadge status={d.status} />
                  {d.vacation_mode ? <VacationBadge /> : null}
                </div>
              </td>
              {isRegistered && (
                <td className="px-5 py-3"><LEDIndicator state={d.led_state} /></td>
              )}
              <td className="px-5 py-3 text-secondary-500 text-xs">{timeAgo(d.last_seen_at)}</td>
              <td className="px-5 py-3 text-xs text-secondary-500">{d.firmware_version || '--'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Devices() {
  const [tab, setTab] = useState('registered');
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // The status/vacation filter lives in the URL so it survives refresh,
  // back/forward, and the dashboard's ?vacation=true deep-link without the
  // dropdown and the URL drifting apart.
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('vacation') === 'true'
    ? 'vacation'
    : (searchParams.get('status') || '');
  const setStatusFilter = useCallback((val) => {
    const next = new URLSearchParams(searchParams);
    next.delete('vacation');
    next.delete('status');
    if (val === 'vacation') next.set('vacation', 'true');
    else if (val) next.set('status', val);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  // Separate counts for tab badges
  const [registeredCount, setRegisteredCount] = useState(0);
  const [unregisteredCount, setUnregisteredCount] = useState(0);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, provisioning: tab };
      if (search) params.search = search;
      // "vacation" is not a status enum value — route it to the vacation overlay filter.
      if (statusFilter === 'vacation') params.vacation = 'true';
      else if (statusFilter) params.status = statusFilter;
      const { data } = await api.get('/devices', { params });
      setDevices(data.data);
      setPagination(data.pagination);
    } catch {} finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, tab]);

  // Fetch tab counts (fire-and-forget, no loading state)
  const fetchCounts = useCallback(async () => {
    try {
      const [reg, unreg] = await Promise.all([
        api.get('/devices', { params: { page: 1, limit: 1, provisioning: 'registered' } }),
        api.get('/devices', { params: { page: 1, limit: 1, provisioning: 'unregistered' } }),
      ]);
      setRegisteredCount(reg.data.pagination.total);
      setUnregisteredCount(unreg.data.pagination.total);
    } catch {}
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  useWS(
    ['device_status_change', 'telemetry_update', 'device:vacation_mode_changed'],
    useCallback(() => { fetchDevices(); fetchCounts(); }, [fetchDevices, fetchCounts])
  );

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const switchTab = (newTab) => {
    setTab(newTab);
    setPage(1);
    setStatusFilter('');
    setSearchInput('');
    setSearch('');
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Devices</h1>
          <p className="text-secondary-500 text-sm mt-1">{pagination.total || 0} devices in this view</p>
        </div>
      </div>

      {/* Tabs: Registered / Unregistered */}
      <div className="flex gap-1 border-b border-secondary-200">
        <button
          onClick={() => switchTab('registered')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            tab === 'registered'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-secondary-500 hover:text-secondary-700'
          }`}
        >
          Registered
          <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-600">{registeredCount}</span>
        </button>
        <button
          onClick={() => switchTab('unregistered')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            tab === 'unregistered'
              ? 'border-amber-600 text-amber-700'
              : 'border-transparent text-secondary-500 hover:text-secondary-700'
          }`}
        >
          Unregistered
          <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-amber-50 text-amber-600">{unregisteredCount}</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          placeholder={tab === 'unregistered' ? 'Search by MAC address, device ID, serial...' : 'Search devices, patients...'}
          className="flex-1 px-4 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
        />
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-4 py-2 border border-secondary-200 rounded-lg text-sm text-secondary-700 focus:ring-2 focus:ring-primary-500 outline-none">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="maintenance">Maintenance</option>
          <option value="vacation">On Vacation</option>
        </select>
      </div>

      {/* Unregistered helper text */}
      {tab === 'unregistered' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          These devices have connected to the system but registration is not yet complete.
          Search by the MAC address (printed on the box) to find a device and help the user finish setup.
          Click a device to view details and associate it with an account.
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <DeviceTable devices={devices} loading={loading} tab={tab} />

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-5 py-3 border-t border-secondary-100 flex items-center justify-between">
            <p className="text-sm text-secondary-500">Page {pagination.page} of {pagination.totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page >= pagination.totalPages}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
